/**
 * Video Stories (Instagram-Style)
 * Web Component: <video-stories>
 *
 * Gerencia o modal fullscreen com carousel de videos,
 * barras de progresso segmentadas, navegacao por tap/swipe/teclado,
 * lazy loading nativo (preload="none") e estado "viewed" em sessionStorage.
 */
if (!customElements.get('video-stories')) {
  class RkVideoStories extends HTMLElement {
    constructor() {
      super();
      this._triggers = [];
      this._storageKey = '';
      this._viewedSet = new Set();
      this._raf = null;
      this._isMuted = true;
      this._carousel = null;
      this._swiper = null;
      this._modalEl = null;
      this._modalDiv = null;
      this._stage = null;
      this._videos = [];
      this._progressSegments = [];
      this._activeIndex = -1;

      this._onCarouselInit = this._onCarouselInit.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this._blockId = this.dataset.blockId || 'default';
      this._modalId = 'rk-vs-modal-' + this._blockId;
      this._storageKey = 'rk-vs-viewed-' + this._blockId;
      this._triggers = [...this.querySelectorAll('[data-story-index]')];

      // Move modal to document.body to escape section stacking contexts
      this._modalEl = this.querySelector('modal-component');
      if (this._modalEl) {
        document.body.appendChild(this._modalEl);
      }

      // Cache elements inside modal (now in body)
      this._modalDiv = this._modalEl?.querySelector('.modal');
      this._stage = this._modalEl?.querySelector('.video-stories__stage');
      this._carousel = this._modalEl?.querySelector('carousel-component');
      this._progressSegments = [...(this._modalEl?.querySelectorAll('.video-stories__progress-segment') || [])];
      this._sidebarBtns = [...(this._modalEl?.querySelectorAll('[data-sidebar-index]') || [])];

      this._restoreViewedState();
      this._bindThumbnails();
      this._bindModalControls();
      this._bindSidebar();
      this._initCarousel();
    }

    disconnectedCallback() {
      this._stopProgressLoop();
      document.removeEventListener('keydown', this._onKeyDown);
      if (this._modalEl?.parentNode === document.body) {
        document.body.removeChild(this._modalEl);
      }
    }

    /* ----------------------------------------------------------------
       Carousel init
       ---------------------------------------------------------------- */

    _initCarousel() {
      if (!this._carousel) return;

      // Swiper may already be initialized (CDN cached)
      if (this._carousel.swiper) {
        this._onCarouselReady(this._carousel.swiper);
      }
      this._carousel.addEventListener('carousel:init', this._onCarouselInit);
    }

    _onCarouselInit(e) {
      if (e.detail?.swiper && !this._swiper) {
        this._onCarouselReady(e.detail.swiper);
      }
    }

    _onCarouselReady(swiper) {
      this._swiper = swiper;
      this._cacheVideos();

      this._swiper.on('slideChange', () => {
        this._activateSlide(this._swiper.activeIndex);
      });
    }

    _cacheVideos() {
      this._videos = [...this._modalEl.querySelectorAll('.video-stories__video')];
    }

    /* ----------------------------------------------------------------
       Thumbnails
       ---------------------------------------------------------------- */

    _bindThumbnails() {
      this._triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const index = parseInt(trigger.dataset.storyIndex, 10);
          this._openStory(index);
        });
      });
    }

    _openStory(index) {
      this._pendingIndex = index;

      // Open the modal directly (bypass modal-component open triggers)
      if (this._modalDiv) {
        this._modalDiv.classList.add('modal--active');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this._onKeyDown);

        // Dispatch event for consistency
        this._modalEl.dispatchEvent(
          new CustomEvent('modal:open', {
            bubbles: true,
            detail: { id: this._modalId, type: 'custom' },
          })
        );

        // Wait for CSS transition + layout to complete, then init Swiper
        this._onModalOpened();
      }
    }

    /* ----------------------------------------------------------------
       Modal open / close
       ---------------------------------------------------------------- */

    _onModalOpened() {
      const index = this._pendingIndex || 0;
      this._pendingIndex = null;

      // Use transitionend to wait for modal to be fully visible
      const initSlides = () => {
        this._cacheVideos();

        if (this._swiper) {
          this._swiper.update();
          this._swiper.slideTo(index, 0);
          // Second update after slideTo to ensure dimensions are correct
          this._swiper.update();
        }

        this._activateSlide(index);
      };

      // Listen for transition end on the modal div
      const onTransitionEnd = (e) => {
        if (e.target !== this._modalDiv) return;
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      };

      this._modalDiv.addEventListener('transitionend', onTransitionEnd);

      // Fallback: if no transition fires (e.g. reduced motion), run after 350ms
      setTimeout(() => {
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      }, 350);
    }

    _closeModal() {
      this._deactivateCurrentSlide();
      this._resetAllProgress();
      this._activeIndex = -1;

      document.removeEventListener('keydown', this._onKeyDown);

      if (this._modalDiv) {
        this._modalDiv.classList.remove('modal--active');
        document.body.style.overflow = '';

        this._modalEl.dispatchEvent(
          new CustomEvent('modal:close', {
            bubbles: true,
            detail: { id: this._modalId, type: 'custom' },
          })
        );
      }
    }

    /* ----------------------------------------------------------------
       Slide activation / video control
       ---------------------------------------------------------------- */

    _activateSlide(index) {
      if (index === this._activeIndex) return;

      this._deactivateCurrentSlide();
      this._activeIndex = index;

      const video = this._videos[index];
      if (!video) return;

      // Update progress bars
      this._progressSegments.forEach((seg, i) => {
        const fill = seg.querySelector('.video-stories__progress-fill');
        if (i < index) {
          seg.classList.add('is-complete');
          fill.style.width = '100%';
        } else {
          seg.classList.remove('is-complete');
          fill.style.width = '0%';
        }
      });

      video.muted = this._isMuted;
      video.currentTime = 0;

      // preload="none" means browser hasn't fetched anything yet.
      // Calling load() + play() triggers the fetch.
      const tryPlay = () => {
        video.play().catch(() => {
          // Autoplay blocked — force muted and retry
          video.muted = true;
          this._isMuted = true;
          this._syncMuteUI();
          video.play().catch(() => {});
        });
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.load();
        video.addEventListener('canplay', tryPlay, { once: true });
      }

      this._startProgressLoop();

      // Update sidebar active state
      this._syncSidebar(index);

      // Mark as viewed
      this._viewedSet.add(index);
      this._markViewed(index);
      this._saveViewedState();

      // Auto-advance on video end
      video._onEnded = () => this._onVideoEnded();
      video.addEventListener('ended', video._onEnded);
    }

    _deactivateCurrentSlide() {
      if (this._activeIndex < 0) return;

      const video = this._videos[this._activeIndex];
      if (video) {
        video.pause();
        if (video._onEnded) {
          video.removeEventListener('ended', video._onEnded);
          video._onEnded = null;
        }
      }

      this._stopProgressLoop();
    }

    _onVideoEnded() {
      const nextIndex = this._activeIndex + 1;
      if (nextIndex < this._videos.length) {
        if (this._swiper) this._swiper.slideNext();
      } else {
        this._closeModal();
      }
    }

    /* ----------------------------------------------------------------
       Progress bars
       ---------------------------------------------------------------- */

    _startProgressLoop() {
      this._stopProgressLoop();

      const update = () => {
        const video = this._videos[this._activeIndex];
        if (!video || video.paused) {
          this._raf = requestAnimationFrame(update);
          return;
        }

        const pct = (video.currentTime / video.duration) * 100 || 0;
        const segment = this._progressSegments[this._activeIndex];
        if (segment) {
          segment.querySelector('.video-stories__progress-fill').style.width = pct + '%';
        }

        this._raf = requestAnimationFrame(update);
      };

      this._raf = requestAnimationFrame(update);
    }

    _stopProgressLoop() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _resetAllProgress() {
      this._progressSegments.forEach((seg) => {
        seg.classList.remove('is-complete');
        seg.querySelector('.video-stories__progress-fill').style.width = '0%';
      });
    }

    /* ----------------------------------------------------------------
       Desktop sidebar
       ---------------------------------------------------------------- */

    _bindSidebar() {
      this._sidebarBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.sidebarIndex, 10);
          if (this._swiper && index !== this._activeIndex) {
            this._swiper.slideTo(index);
          }
        });
      });
    }

    _syncSidebar(index) {
      this._sidebarBtns.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === index);
      });
    }

    /* ----------------------------------------------------------------
       Modal controls
       ---------------------------------------------------------------- */

    _bindModalControls() {
      // Close button
      const closeBtn = this._modalEl?.querySelector('.video-stories__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeModal());

      // Overlay close
      const overlay = this._modalEl?.querySelector('.modal__overlay');
      if (overlay) overlay.addEventListener('click', () => this._closeModal());

      // Mute toggle
      const muteBtn = this._modalEl?.querySelector('.video-stories__mute');
      if (muteBtn) muteBtn.addEventListener('click', () => this._toggleMute());

      // Tap zones
      const tapPrev = this._modalEl?.querySelector('[data-tap="prev"]');
      const tapNext = this._modalEl?.querySelector('[data-tap="next"]');
      if (tapPrev) tapPrev.addEventListener('click', () => this._goPrev());
      if (tapNext) tapNext.addEventListener('click', () => this._goNext());
    }

    _toggleMute() {
      this._isMuted = !this._isMuted;
      const video = this._videos[this._activeIndex];
      if (video) video.muted = this._isMuted;
      this._syncMuteUI();
    }

    _syncMuteUI() {
      if (this._stage) {
        this._stage.classList.toggle('is-unmuted', !this._isMuted);
      }
    }

    _goPrev() {
      if (this._activeIndex <= 0) return;
      if (this._swiper) this._swiper.slidePrev();
    }

    _goNext() {
      if (this._activeIndex >= this._videos.length - 1) {
        this._closeModal();
        return;
      }
      if (this._swiper) this._swiper.slideNext();
    }

    /* ----------------------------------------------------------------
       Keyboard
       ---------------------------------------------------------------- */

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this._closeModal();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._goNext();
      } else if (e.key === 'm') {
        e.preventDefault();
        this._toggleMute();
      }
    }

    /* ----------------------------------------------------------------
       Viewed state (sessionStorage)
       ---------------------------------------------------------------- */

    _restoreViewedState() {
      try {
        const stored = sessionStorage.getItem(this._storageKey);
        if (stored) {
          JSON.parse(stored).forEach((idx) => {
            this._viewedSet.add(idx);
            this._markViewed(idx);
          });
        }
      } catch {}
    }

    _saveViewedState() {
      try {
        sessionStorage.setItem(this._storageKey, JSON.stringify([...this._viewedSet]));
      } catch {}
    }

    _markViewed(index) {
      const trigger = this._triggers.find(
        (t) => parseInt(t.dataset.storyIndex, 10) === index
      );
      if (trigger) trigger.classList.add('is-viewed');
    }
  }

  customElements.define('video-stories', RkVideoStories);
}
